// src/pages/components/NavbarPos.js

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  AppBar,
  Toolbar,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  AccountCircle as AccountCircleIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  PowerSettingsNew as LogoutIcon,
  Inventory as InventoryIcon,
  FileUpload as FileUploadIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  SupportAgent as SupportAgentIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import logo from '../assets/logo.png';
import api from '../../api/api';
import ReposicionTabacoModal from '../modals/ReposicionTabacoModal';

const StyledAppBar = styled(AppBar)`
  background: linear-gradient(90deg, #1565c0 0%, #1e88e5 100%);
  z-index: 1300;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
`;

const StyledToolbar = styled(Toolbar)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  min-height: 56px;

  @media (max-width:600px) {
    flex-wrap: wrap;
    padding: 8px;
    min-height: auto;
  }
`;

const Left = styled(Box)`
  display: flex;
  align-items: center;
`;

const Center = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width:600px) {
    width: 100%;
    justify-content: space-around;
    gap: 6px;
    margin: 8px 0;
    flex-wrap: wrap;
  }
`;

const Right = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Logo = styled.img`
  height: 32px;
  margin-right: 12px;

  @media (max-width:600px) {
    height: 28px;
    margin-right: 8px;
  }
`;


const NAVBAR_CAJA_CACHE_PREFIX = 'IntegraNubePOS:navbar:caja';

const getTenantScope = () => {
  try {
    const flat = String(localStorage.getItem('tenantid') || '').trim();
    if (flat) return flat;

    const tenant = JSON.parse(localStorage.getItem('tenant') || 'null');
    const fromTenant = String(tenant?.tenantid || tenant?.id || '').trim();
    if (fromTenant) return fromTenant;

    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const fromUser = String(user?.tenantid || '').trim();
    if (fromUser) return fromUser;
  } catch (_) {
    // noop
  }

  return 'default';
};

const getCajaCacheKey = () => `${NAVBAR_CAJA_CACHE_PREFIX}:${getTenantScope()}`;

const readCajaCache = () => {
  try {
    const raw = localStorage.getItem(getCajaCacheKey());
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (typeof parsed?.abierta !== 'boolean') return null;

    return parsed;
  } catch (_) {
    return null;
  }
};

const writeCajaCache = ({ abierta, cajaId = null }) => {
  try {
    localStorage.setItem(
      getCajaCacheKey(),
      JSON.stringify({
        abierta: Boolean(abierta),
        cajaId: cajaId || null,
        confirmedAt: new Date().toISOString(),
      })
    );
  } catch (_) {
    // Si localStorage no está disponible, el POS sigue usando el estado en memoria.
  }
};

const normalizeCajaEstado = data => {
  const caja =
    data?.caja ||
    data?.data?.caja ||
    (data?.data && typeof data.data === 'object' ? data.data : null) ||
    null;

  const estadoRaw = String(
    data?.estado ||
    data?.estado_caja ||
    caja?.estado ||
    ''
  ).trim().toLowerCase();

  const explicitOpen =
    data?.abierta === true ||
    data?.isAbierta === true ||
    data?.caja_abierta === true ||
    estadoRaw === 'abierta' ||
    estadoRaw === 'abierto' ||
    estadoRaw === 'open';

  const explicitClosed =
    data?.abierta === false ||
    data?.isAbierta === false ||
    data?.caja_abierta === false ||
    estadoRaw === 'cerrada' ||
    estadoRaw === 'cerrado' ||
    estadoRaw === 'closed';

  const cajaId =
    caja?.id ??
    caja?.id_caja ??
    data?.caja_id ??
    data?.id_caja ??
    null;

  let abierta = false;

  if (explicitOpen) {
    abierta = true;
  } else if (explicitClosed) {
    abierta = false;
  } else {
    // Fallback para respuestas antiguas donde solo venía la caja activa.
    abierta = Boolean(cajaId);
  }

  return {
    abierta,
    cajaId: cajaId || null,
  };
};

export default function NavbarPos({
  onToggleCaja,
  onF1,
  onF2,
  onF3,
  onF4,
  onF8,
  onF9,
  onF10,
  cajaAbierta: cajaAbiertaProp,
  loadingEstado = false,
  posConnectionStatus = null,
  posConnectionLabel = '',
  posConnectionColor = 'success',
  posConnectionTooltip = '',
  offlineQueueCount = 0,
  onConnectionClick,
}) {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const initialCajaCache = readCajaCache();

  const [cajaAbierta, setCajaAbierta] = useState(
    typeof initialCajaCache?.abierta === 'boolean' ? initialCajaCache.abierta : false
  );
  const [cajaId, setCajaId] = useState(initialCajaCache?.cajaId || null);
  const [loading, setLoading] = useState(!initialCajaCache);
  const [estadoFuente, setEstadoFuente] = useState(
    initialCajaCache ? 'cache' : 'desconocido'
  );
  const [propEstadoConfirmado, setPropEstadoConfirmado] = useState(
    typeof cajaAbiertaProp === 'boolean' && !loadingEstado
  );
  const [apiCajaDisponible, setApiCajaDisponible] = useState(null);
  const [reposOpen, setReposOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '' });

  // La sesión puede estar guardada en sessionStorage o localStorage.
  // Priorizamos sessionStorage para mostrar exactamente al usuario autenticado actual.
  const usuario = (() => {
    const raw = sessionStorage.getItem('user') || localStorage.getItem('user');

    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  })();

  const usuarioNombre = (() => {
    if (!usuario || typeof usuario !== 'object') return 'Usuario POS';

    const nombreCompleto = [usuario.nombres, usuario.apellidos]
      .filter(Boolean)
      .join(' ')
      .trim();

    return (
      usuario.name ||
      usuario.nombre ||
      usuario.fullName ||
      usuario.fullname ||
      nombreCompleto ||
      usuario.username ||
      usuario.email ||
      'Usuario POS'
    );
  })();

  /*
   * La fuente principal del header es POS.js.
   * Cuando POS.js termina de resolver caja (servidor o contingencia),
   * su prop deja de estar "cargando" y pasa a ser autoritativa.
   *
   * Importante: si luego la API cae y POS.js vuelve a consultar,
   * NO ocultamos el estado conocido con un spinner ni lo reemplazamos por "cerrada".
   */
  useEffect(() => {
    if (typeof cajaAbiertaProp !== 'boolean' || loadingEstado) return;

    setPropEstadoConfirmado(true);
  }, [cajaAbiertaProp, loadingEstado]);

  const fetchEstado = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);

    try {
      const { data } = await api.get('/pos_cajas/estado', {
        // El header nunca debe quedarse esperando el timeout global.
        timeout: 5000,
      });

      const estado = normalizeCajaEstado(data);

      setCajaAbierta(estado.abierta);
      setCajaId(estado.cajaId);
      setEstadoFuente('api');
      setApiCajaDisponible(true);

      // Solo un resultado realmente recibido desde API renueva este cache.
      writeCajaCache(estado);

      return {
        ...estado,
        source: 'api',
      };
    } catch (e) {
      console.warn(
        '[NavbarPos] No se pudo verificar caja por API; se conserva el último estado conocido:',
        e?.response?.status || e?.code || e?.message || e
      );

      setApiCajaDisponible(false);

      /*
       * NUNCA convertir una falla de API en "Caja Cerrada".
       * Si el padre aún no ha confirmado su estado, usamos el último
       * estado exitoso guardado por este header.
       */
      const cached = readCajaCache();

      if (cached) {
        setCajaAbierta(Boolean(cached.abierta));
        setCajaId(cached.cajaId || null);
        setEstadoFuente('cache');

        return {
          abierta: Boolean(cached.abierta),
          cajaId: cached.cajaId || null,
          source: 'cache',
        };
      }

      return {
        abierta: null,
        cajaId: null,
        source: 'unknown',
      };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Primera comprobación: si ya existe cache, se hace silenciosamente.
    fetchEstado({ silent: Boolean(initialCajaCache) });

    const onCajaActualizada = () => {
      fetchEstado({ silent: true });
    };

    window.addEventListener('cajaActualizada', onCajaActualizada);

    return () => {
      window.removeEventListener('cajaActualizada', onCajaActualizada);
    };
    // initialCajaCache solo representa el snapshot de montaje.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEstado]);

  /*
   * Recuperación propia del header.
   * POS.js ya tiene recuperación automática, pero este sondeo evita que el
   * header dependa de ella para volver a confirmar su cache/cajaId.
   */
  useEffect(() => {
    if (apiCajaDisponible !== false) return undefined;

    const timer = window.setInterval(() => {
      fetchEstado({ silent: true });
    }, 10000);

    return () => window.clearInterval(timer);
  }, [apiCajaDisponible, fetchEstado]);

  const warn = msg => setSnack({ open: true, msg });

  /*
   * Si POS.js ya confirmó caja, esa prop gana siempre.
   * Antes de esa confirmación usamos API/cache interno del Navbar.
   */
  const usarEstadoPadre =
    typeof cajaAbiertaProp === 'boolean' && propEstadoConfirmado;

  const cajaAbiertaReal = usarEstadoPadre
    ? Boolean(cajaAbiertaProp)
    : Boolean(cajaAbierta);

  const estadoConocido =
    usarEstadoPadre || estadoFuente === 'api' || estadoFuente === 'cache';

  // Solo mostramos spinner cuando todavía NO conocemos ningún estado.
  const loadingReal = !estadoConocido && (loadingEstado || loading);

  const cajaLabel = cajaAbiertaReal ? 'Caja Abierta' : 'Caja Cerrada';

  const cajaTooltip =
    apiCajaDisponible === false
      ? `${cajaLabel} · último estado confirmado (API no disponible)`
      : apiCajaDisponible === true
        ? `${cajaLabel} · estado confirmado`
        : cajaLabel;

  const handleToggle = async () => {
    if (!onToggleCaja) return;

    try {
      /*
       * onToggleCaja abre el modal. No consultamos inmediatamente después:
       * la apertura/cierre real dispara "cajaActualizada" desde POS.js.
       */
      await onToggleCaja();
    } catch (e) {
      console.error('Error toggling caja:', e);
    }
  };

  // safeCall tolera que fn sea undefined
  const safeCall = fn => () => {
    if (!fn) return;
    cajaAbiertaReal ? fn() : warn('Debe abrir caja primero');
  };

  // F1 (Nueva venta) NO requiere caja abierta
  const handleF1 = () => {
    if (onF1) onF1();
  };

  const handleF2 = () => onF2 && onF2(); // siempre habilitado
  const handleF3 = safeCall(onF3);
  const handleF4 = safeCall(onF4);

  const handleF7 = async () => {
    if (!cajaAbiertaReal) {
      warn('Debe abrir caja primero');
      return;
    }

    // Reposición es una operación remota: no se ejecuta en contingencia.
    if (apiCajaDisponible === false) {
      warn('Reposición no disponible mientras la API está en contingencia');
      return;
    }

    if (!cajaId) {
      const estado = await fetchEstado({ silent: true });

      if (!estado?.cajaId) {
        warn('No se pudo identificar la caja activa');
        return;
      }
    }

    setReposOpen(true);
  };

  const handleF8 = safeCall(onF8);
  const handleF9 = safeCall(onF9);
  const handleF10 = safeCall(onF10); // Ingresos/Egresos

  const handleCloseRepos = () => setReposOpen(false);

  const handleConfirmRepos = async detalle => {
    if (!cajaId) {
      warn('No se pudo identificar la caja activa');
      return;
    }

    try {
      await api.post(
        `/pos_cajas/${cajaId}/reposicion`,
        {
          detalle_reposicion: detalle,
        },
        {
          timeout: 8000,
        }
      );

      setApiCajaDisponible(true);
      setReposOpen(false);
    } catch (e) {
      console.error('Error en reposición:', e);
      setApiCajaDisponible(false);
      warn('No se pudo registrar la reposición. La API no está disponible.');
    }
  };

  const handleLogout = () => {
    setLogoutOpen(true);
  };

  const handleCancelLogout = () => {
    setLogoutOpen(false);
  };

  const handleConfirmLogout = () => {
    setLogoutOpen(false);
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  return (
    <>
      <StyledAppBar position="fixed">
        <StyledToolbar variant="dense">
          {/* LEFT */}
          <Left>
            <Logo src={logo} alt="IntegraNube" />
            {loadingReal ? (
              <Tooltip title="Verificando estado de caja">
                <CircularProgress color="inherit" size={22} />
              </Tooltip>
            ) : (
              <Tooltip title={cajaTooltip}>
                <Chip
                  label={cajaLabel}
                  color={cajaAbiertaReal ? 'success' : 'error'}
                  onClick={handleToggle}
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    ...(apiCajaDisponible === false
                      ? {
                          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.7)',
                        }
                      : {}),
                  }}
                  icon={<ReceiptIcon sx={{ color: '#fff' }} />}
                />
              </Tooltip>
            )}

          </Left>

          {/* CENTER – icono + texto corto, Fxx en tooltip */}
          <Center>
            <Tooltip title="F1: Nueva venta" arrow>
              <Button
                size="small"
                startIcon={<ReceiptIcon fontSize="medium" />}
                onClick={handleF1}
                sx={{
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  p: '4px 10px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                F1: Nueva venta
              </Button>
            </Tooltip>

            <Tooltip title="F2: Consultor" arrow>
              <Button
                size="small"
                startIcon={<SupportAgentIcon fontSize="medium" />}
                onClick={handleF2}
                sx={{
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  p: '4px 10px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                F2: Consultor
              </Button>
            </Tooltip>

            <Tooltip title="F3: Buscar productos" arrow>
              <Button
                size="small"
                startIcon={<SearchIcon fontSize="medium" />}
                onClick={handleF3}
                sx={{
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  p: '4px 10px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                F3: Buscar
              </Button>
            </Tooltip>

            <Tooltip title="F7: Reponer tabaco" arrow>
              <Button
                size="small"
                startIcon={<InventoryIcon fontSize="medium" />}
                onClick={handleF7}
                sx={{
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  p: '4px 10px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                F7: Reponer
              </Button>
            </Tooltip>

            <Tooltip title="F10: Ingresos/Egresos" arrow>
              <Button
                size="small"
                startIcon={<AccountBalanceWalletIcon fontSize="medium" />}
                onClick={handleF10}
                sx={{
                  color: '#fff',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  p: '4px 10px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                F10: Ingresos/Egresos
              </Button>
            </Tooltip>
          </Center>

          {/* RIGHT – estado de conexión + cierre de sesión */}
          <Right>
            {posConnectionStatus && (
              <Tooltip title={posConnectionTooltip || posConnectionLabel} arrow>
                <Button
                  variant="contained"
                  color={posConnectionColor}
                  size="small"
                  startIcon={
                    posConnectionStatus === 'syncing' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SyncIcon />
                    )
                  }
                  onClick={onConnectionClick}
                  sx={{
                    borderRadius: 999,
                    fontWeight: 900,
                    textTransform: 'none',
                    minWidth: 132,
                    border: '1px solid transparent',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.14)',
                    ...(posConnectionStatus === 'online'
                      ? {
                          bgcolor: '#EAF4FF',
                          color: '#1565C0',
                          borderColor: 'rgba(255,255,255,0.65)',
                          '& .MuiButton-startIcon': { color: '#1565C0' },
                          '&:hover': {
                            bgcolor: '#FFFFFF',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.18)',
                          },
                        }
                      : posConnectionStatus === 'syncing'
                        ? {
                            bgcolor: '#FFF8E1',
                            color: '#A15C00',
                            borderColor: '#FFE0B2',
                            '& .MuiButton-startIcon': { color: '#A15C00' },
                            '&:hover': { bgcolor: '#FFF3CD' },
                          }
                        : {
                            bgcolor: '#FFEBEE',
                            color: '#C62828',
                            borderColor: '#FFCDD2',
                            '& .MuiButton-startIcon': { color: '#C62828' },
                            '&:hover': { bgcolor: '#FFCDD2' },
                          }),
                  }}
                >
                  {posConnectionLabel}
                  {offlineQueueCount > 0 ? ` · ${offlineQueueCount}` : ''}
                </Button>
              </Tooltip>
            )}

            {usuario && (
              <Tooltip arrow title={`Salir del POS (${usuarioNombre})`}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LogoutIcon fontSize="small" />}
                  onClick={handleLogout}
                  sx={{
                    bgcolor: '#D32F2F',
                    color: '#fff',
                    borderRadius: 999,
                    px: 1.8,
                    minWidth: 88,
                    fontWeight: 900,
                    textTransform: 'none',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.20)',
                    '& .MuiButton-startIcon': {
                      color: '#fff',
                      mr: 0.7,
                    },
                    '&:hover': {
                      bgcolor: '#B71C1C',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.24)',
                    },
                  }}
                >
                  Salir
                </Button>
              </Tooltip>
            )}
          </Right>
        </StyledToolbar>
      </StyledAppBar>

      {/* CONFIRMACIÓN CIERRE DE SESIÓN */}
      <Dialog
        open={logoutOpen}
        onClose={handleCancelLogout}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>
          Cerrar sesión
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} alignItems="center" sx={{ pt: 1, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.main',
                color: '#fff',
              }}
            >
              <AccountCircleIcon sx={{ fontSize: 42 }} />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                Sesión actualmente iniciada por
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, fontWeight: 900 }}>
                {usuarioNombre}
              </Typography>
            </Box>

            {cajaAbiertaReal ? (
              <Alert severity="warning" sx={{ width: '100%', textAlign: 'left' }}>
                La caja se encuentra abierta. Cerrar sesión no cerrará la caja.
              </Alert>
            ) : null}

            <Typography variant="body1">
              ¿Realmente desea cerrar esta sesión del POS?
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button
            onClick={handleCancelLogout}
            variant="outlined"
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            color="error"
            startIcon={<LogoutIcon />}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
          >
            Salir
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning">{snack.msg}</Alert>
      </Snackbar>

      {/* MODAL REPOSICIÓN */}
      <ReposicionTabacoModal
        open={reposOpen}
        cajaId={cajaId}
        onConfirm={handleConfirmRepos}
        onCancel={handleCloseRepos}
      />
    </>
  );
}
